// =============================================================================
// CookieConsentBanner.js, Phase 38.7
//
// A minimal, professional cookie banner.
//
// Behaviour:
//   • Shown only when localStorage `2120_cookie_consent` is unset.
//   • Both [Accept] and [Decline] dismiss the banner permanently for this
//     browser (per founder direction). The decision is persisted as
//     'accepted' or 'declined' so the public-site analytics layer can
//     read it later if it wants to gate GA4 / PostHog.
//   • In the signed-in app, no analytics tooling currently relies on this
//     value, the banner is shown primarily for legal disclosure parity
//     with the public website.
//   • Renders nothing on SSR. Safe to import anywhere.
//
// Styling: calm card pinned to the bottom of the viewport on mobile;
// bottom-right on desktop. Respects safe-area-inset for iOS.
// =============================================================================
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie } from 'lucide-react';

const STORAGE_KEY = '2120_cookie_consent';

/**
 * Read the persisted consent value.
 * Returns one of: 'accepted' | 'declined' | null.
 * Anything else (corrupted storage, private mode) is treated as null.
 */
export function getCookieConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to after first paint so the banner doesn’t shift the LCP.
    const id = window.setTimeout(() => {
      if (getCookieConsent() === null) setVisible(true);
    }, 400);
    return () => window.clearTimeout(id);
  }, []);

  const persist = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* private-mode users: we still hide the banner for this session */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-4 pb-3 sm:pb-4 pointer-events-none"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}
      data-testid="cookie-consent-banner"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="mx-auto sm:ml-auto sm:mr-4 max-w-md pointer-events-auto rounded-2xl border border-border/70 bg-card/95 backdrop-blur shadow-brand-lg p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
            <Cookie className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground leading-relaxed">
              We use cookies to keep you signed in and remember your preferences. By continuing,
              you agree to our use of cookies.{' '}
              <Link
                to="/cookies"
                className="underline underline-offset-2 hover:no-underline"
                data-testid="cookie-banner-policy-link"
              >
                See our Cookie Policy
              </Link>
              .
            </p>
            <div className="mt-3 flex items-center gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-xl text-xs"
                onClick={() => persist('declined')}
                data-testid="cookie-banner-decline"
              >
                Decline
              </Button>
              <Button
                size="sm"
                className="rounded-xl text-xs"
                onClick={() => persist('accepted')}
                data-testid="cookie-banner-accept"
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Named export for tree-shaking flexibility; default export for default-import
// ergonomics elsewhere in the codebase.
export { CookieConsentBanner };
